import { Text, Flex, Icon, IconName, Colors } from '@upsales/components';
import type { PreMeetingResearch } from 'App/resources/Model/AISummary';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from '../../Helpers/translate';
import { renderMarkdown } from 'Helpers/markdown';
import React from 'react';

import './PreMeetingResearchView.scss';

interface SectionProps {
	title: string;
	children: React.ReactNode;
}

interface BulletPointProps {
	children: React.ReactNode;
	icon: IconName;
	iconColor: Colors;
}

interface PreMeetingResearchViewProps {
	research: PreMeetingResearch | string | null;
	forceEnglish?: boolean;
}

function Section({ title, children }: SectionProps) {
	return (
		<Flex direction="column" gap="u3">
			<Text size="xs" bold color="neutral-450">
				{title.toUpperCase()}
			</Text>
			{children}
		</Flex>
	);
}

function BulletPoint({ children, icon, iconColor }: BulletPointProps) {
	const classes = new BemClass('PreMeetingResearchView');
	return (
		<Flex gap="8" alignItems="center" spacing="pl2">
			{icon ? <Icon name={icon} color={iconColor} /> : <span className={classes.elem('bulletDot').b()} />}
			<Text size="sm" color="neutral-600" style={{ flex: 1, lineHeight: 1.5 }}>
				{children}
			</Text>
		</Flex>
	);
}

const parsePreMeetingResearch = (data: PreMeetingResearch | string | null): PreMeetingResearch | null => {
	if (!data) {
		return null;
	}

	if (typeof data === 'object' && data !== null) {
		return data as PreMeetingResearch;
	}

	if (typeof data === 'string') {
		try {
			return JSON.parse(data) as PreMeetingResearch;
		} catch (error) {
			console.error('Failed to parse pre-meeting research JSON:', error);
			return null;
		}
	}

	return null;
};

export default function PreMeetingResearchView({ research: rawResearch, forceEnglish }: PreMeetingResearchViewProps) {
	const { t } = useTranslation(undefined, { lng: forceEnglish ? 'en-US' : undefined });
	const classes = new BemClass('PreMeetingResearchView');

	const research = parsePreMeetingResearch(rawResearch);

	if (!research) {
		return (
			<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4">
				<Text>{t('preMeetingResearch.noResearchAvailable')}</Text>
			</Flex>
		);
	}

	return (
		<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4">
			<Section title={t('preMeetingResearch.summary')}>
				<Text color="neutral-700" style={{ lineHeight: 1.5, letterSpacing: '0.3px' }}>
					{research.summary}
				</Text>
			</Section>

			{research.company_overview ? (
				<Section title={t('preMeetingResearch.companyOverview')}>
					<Text size="sm" color="neutral-600" style={{ lineHeight: 1.5 }}>
						{research.company_overview}
					</Text>
				</Section>
			) : null}

			{research.relationship_summary ? (
				<Section title={t('preMeetingResearch.relationshipSummary')}>
					<Text size="sm" color="neutral-600" style={{ lineHeight: 1.5 }}>
						{research.relationship_summary}
					</Text>
				</Section>
			) : null}

			{research.conversation_starters && research.conversation_starters.length > 0 ? (
				<Section title={t('preMeetingResearch.conversationStarters')}>
					<Flex direction="column" gap="u6">
						{research.conversation_starters.map((starter, idx) => (
							<BulletPoint key={`starter-${idx}`} icon="comments" iconColor="info-300">
								{starter}
							</BulletPoint>
						))}
					</Flex>
				</Section>
			) : null}

			{research.discovery_questions && research.discovery_questions.length > 0 ? (
				<Section title={t('preMeetingResearch.discoveryQuestions')}>
					<Flex direction="column" gap="u6">
						{research.discovery_questions.map((question, idx) => (
							<BulletPoint key={`question-${idx}`} icon="question-circle" iconColor="beta-400">
								{question}
							</BulletPoint>
						))}
					</Flex>
				</Section>
			) : null}

			{research.talking_points && research.talking_points.length > 0 ? (
				<Section title={t('preMeetingResearch.talkingPoints')}>
					<Flex direction="column" gap="u6">
						{research.talking_points.map((point, idx) => (
							<BulletPoint key={`point-${idx}`} icon="comments" iconColor="neutral-650">
								{point}
							</BulletPoint>
						))}
					</Flex>
				</Section>
			) : null}

			{research.topics_to_avoid && research.topics_to_avoid.length > 0 ? (
				<Section title={t('preMeetingResearch.topicsToAvoid')}>
					<Flex direction="column" gap="u6" className={classes.elem('warningSection').b()}>
						{research.topics_to_avoid.map((topic, idx) => (
							<BulletPoint key={`avoid-${idx}`} icon="exclamation-triangle" iconColor="alert-400">
								{topic}
							</BulletPoint>
						))}
					</Flex>
				</Section>
			) : null}

			{research.positive_indicators && research.positive_indicators.length > 0 ? (
				<Section title={t('preMeetingResearch.positiveIndicators')}>
					<Flex direction="column" gap="u6" className={classes.elem('positiveSection').b()}>
						{research.positive_indicators.map((indicator, idx) => (
							<BulletPoint key={`positive-${idx}`} icon="check-circle" iconColor="success-400">
								{indicator}
							</BulletPoint>
						))}
					</Flex>
				</Section>
			) : null}

			{research.risk_factors && research.risk_factors.length > 0 ? (
				<Section title={t('preMeetingResearch.riskFactors')}>
					<Flex direction="column" gap="u6" className={classes.elem('riskSection').b()}>
						{research.risk_factors.map((risk, idx) => (
							<BulletPoint key={`risk-${idx}`} icon="exclamation-circle" iconColor="danger-400">
								{risk}
							</BulletPoint>
						))}
					</Flex>
				</Section>
			) : null}

			{research.additional_sections?.map((section, idx) => (
				<Section key={`additional-${idx}`} title={section.title}>
					<div
						className={classes.elem('markdown').b()}
						dangerouslySetInnerHTML={{ __html: renderMarkdown(section.content) }}
					/>
				</Section>
			))}

			{research.recommendation ? (
				<>
					<hr className={classes.elem('divider').b()} />
					<Section title={t('preMeetingResearch.recommendation')}>
						<Flex direction="column" gap="u2" className={classes.elem('recommendationCard').b()}>
							<Text size="sm" color="neutral-700" style={{ lineHeight: 1.5 }}>
								{research.recommendation}
							</Text>
						</Flex>
					</Section>
				</>
			) : null}
		</Flex>
	);
}
